/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef } from 'react';
import {
  Calendar,
  ShieldCheck,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit2,
  Trash2,
  Camera,
  Upload,
  X,
  User,
  Users,
  Building2,
  FileText,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Layers,
  Award,
  Video,
  RefreshCw,
  Maximize2,
  FileSpreadsheet
} from 'lucide-react';
import { CostCenter, SafetyTalk, SafetyTalkPhoto, SafetyTalkStatus, SafetyTalkAttendee } from '../types.ts';
import { generateSafetyTalkPDF, generateMonthlySafetyTalksPlanPDF } from '../utils/safetyPdfGenerator.ts';
import { SafetyTalksBulkUploadModal } from './SafetyTalksBulkUploadModal.tsx';

interface SafetyTalksPlannerManagerProps {
  activeCostCenter: CostCenter | null;
  safetyTalks: SafetyTalk[];
  onSaveTalk: (talk: SafetyTalk) => void;
  onBatchSaveTalks?: (talks: SafetyTalk[], replaceMonth?: boolean, year?: number, monthIndex?: number) => void;
  onDeleteTalk: (talkId: string) => void;
}

const MONTHS_SPANISH = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// STPS Standard suggested topics for quick safety talks planning
const SUGGESTED_TOPICS = [
  { topic: 'Uso y Conservación del Equipo de Protección Personal (EPP)', standard: 'NOM-017-STPS-2008', area: 'Todas las Áreas' },
  { topic: 'Prevención de Caídas y Trabajos Seguros en Alturas', standard: 'NOM-009-STPS-2011', area: 'Mantenimiento / Alturas' },
  { topic: 'Identificación y Comunicación de Peligros por Sustancias Químicas (HDS / SGA)', standard: 'NOM-018-STPS-2015', area: 'Decapado / Químicos' },
  { topic: 'Seguridad en Maquinaria, Guardas y Dispositivos de Bloqueo (LOTO)', standard: 'NOM-004-STPS-1999', area: 'Hornos / Piro-oxidación' },
  { topic: 'Manejo Seguro de Sustancias Inflamables y Combustibles', standard: 'NOM-005-STPS-1998', area: 'Pintura / Solventes' },
  { topic: 'Prevención de Incendios, Extintores y Rutas de Evacuación', standard: 'NOM-002-STPS-2010', area: 'Planta General' },
  { topic: 'Ergonomía y Manejo Manual de Cargas en Almacén', standard: 'NOM-036-1-STPS-2018', area: 'Almacén y Logística' },
  { topic: 'Señales, Avisos y Colores de Seguridad e Higiene', standard: 'NOM-026-STPS-2008', area: 'Patios y Tránsito' },
  { topic: 'Seguridad en Espacios Confinados y Ventilación', standard: 'NOM-033-STPS-2015', area: 'Mantenimiento Pesado' },
  { topic: 'Manejo y Almacenamiento Seguro de Materiales', standard: 'NOM-006-STPS-2014', area: 'Embarques y Descarga' },
  { topic: 'Factores de Riesgo Psicosocial y Clima Laboral Seguro', standard: 'NOM-035-STPS-2018', area: 'Administración y Planta' },
  { topic: 'Metodología 5S: Orden y Limpieza como Barrera de Seguridad', standard: 'Buenas Prácticas ISO 45001', area: 'Piso de Producción' }
];

export default function SafetyTalksPlannerManager({
  activeCostCenter,
  safetyTalks,
  onSaveTalk,
  onBatchSaveTalks,
  onDeleteTalk
}: SafetyTalksPlannerManagerProps) {
  // Navigation & Period
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(7); // 7 = Agosto
  const [viewMode, setViewMode] = useState<'calendar' | 'cross' | 'history'>('calendar');

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'aplicada' | 'programada' | 'no_realizada'>('all');

  // Modal States
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [selectedTalkForDetail, setSelectedTalkForDetail] = useState<SafetyTalk | null>(null);

  // Form State
  const [formTalk, setFormTalk] = useState<Partial<SafetyTalk>>({});
  const [photosList, setPhotosList] = useState<SafetyTalkPhoto[]>([]);
  const [attendeesList, setAttendeesList] = useState<SafetyTalkAttendee[]>([]);
  const [isAttendeesExpanded, setIsAttendeesExpanded] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingMonthlyPdf, setIsGeneratingMonthlyPdf] = useState(false);

  // Lightbox Zoom State
  const [lightboxPhoto, setLightboxPhoto] = useState<SafetyTalkPhoto | null>(null);

  // Camera Live Modal State
  const [isCameraActive, setIsCameraActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Hidden file input refs
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const monthName = MONTHS_SPANISH[selectedMonthIndex];
  const formattedMonthYear = `${monthName} ${selectedYear}`;
  const costCenterTitle = activeCostCenter ? activeCostCenter.name : 'Sede Bajío - Salamanca';

  // Days count in current month
  const daysInCurrentMonth = useMemo(() => {
    return new Date(selectedYear, selectedMonthIndex + 1, 0).getDate();
  }, [selectedYear, selectedMonthIndex]);

  // Is Day in Future helper
  const isDateInFuture = (day: number) => {
    const today = new Date();
    const curY = today.getFullYear();
    const curM = today.getMonth();
    const curD = today.getDate();

    if (selectedYear > curY) return true;
    if (selectedYear < curY) return false;
    if (selectedMonthIndex > curM) return true;
    if (selectedMonthIndex < curM) return false;
    return day > curD;
  };

  // Talks filtered by cost center and selected month
  const monthTalks = useMemo(() => {
    const monthStr = String(selectedMonthIndex + 1).padStart(2, '0');
    const yearMonthPrefix = `${selectedYear}-${monthStr}`;

    return safetyTalks.filter((talk) => {
      const matchCostCenter = !activeCostCenter || !talk.costCenterId || talk.costCenterId === activeCostCenter.id;
      const matchDate = talk.date && talk.date.startsWith(yearMonthPrefix);
      return matchCostCenter && matchDate;
    });
  }, [safetyTalks, activeCostCenter, selectedYear, selectedMonthIndex]);

  // Mapping days 1..daysInCurrentMonth with their corresponding talks
  const calendarDays = useMemo(() => {
    const days: { day: number; dateStr: string; talk?: SafetyTalk; isFuture: boolean }[] = [];
    const monthStr = String(selectedMonthIndex + 1).padStart(2, '0');

    for (let d = 1; d <= daysInCurrentMonth; d++) {
      const dayStr = String(d).padStart(2, '0');
      const dateStr = `${selectedYear}-${monthStr}-${dayStr}`;
      
      const found = monthTalks.find((t) => {
        if (t.day === d) return true;
        return t.date === dateStr;
      });

      const isFuture = isDateInFuture(d);
      days.push({ day: d, dateStr, talk: found, isFuture });
    }
    return days;
  }, [daysInCurrentMonth, monthTalks, selectedYear, selectedMonthIndex]);

  // Monthly Metrics KPIs
  const metrics = useMemo(() => {
    const totalMonth = monthTalks.length;
    const applied = monthTalks.filter(t => t.status === 'aplicada' || t.status === 'completada').length;
    const scheduled = monthTalks.filter(t => t.status === 'programada').length;
    const pending = monthTalks.filter(t => t.status === 'no_realizada').length;
    const totalAttendees = monthTalks.reduce((acc, t) => acc + (Number(t.attendeesCount) || 0), 0);
    const complianceRate = totalMonth > 0 ? Math.round((applied / totalMonth) * 100) : 0;
    const avgAttendees = applied > 0 ? Math.round(totalAttendees / applied) : 0;

    return {
      totalMonth,
      applied,
      scheduled,
      pending,
      totalAttendees,
      complianceRate,
      avgAttendees
    };
  }, [monthTalks]);

  // Filtered talks for History View
  const filteredHistoryTalks = useMemo(() => {
    return monthTalks.filter(t => {
      const matchSearch = searchTerm === '' || 
        t.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.facilitator.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.area.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.standardCode && t.standardCode.toLowerCase().includes(searchTerm.toLowerCase()));
      
      if (!matchSearch) return false;

      if (filterStatus === 'all') return true;
      if (filterStatus === 'aplicada') return t.status === 'aplicada' || t.status === 'completada';
      if (filterStatus === 'programada') return t.status === 'programada';
      if (filterStatus === 'no_realizada') return t.status === 'no_realizada';
      return true;
    });
  }, [monthTalks, searchTerm, filterStatus]);

  // Month navigation handlers
  const handlePrevMonth = () => {
    if (selectedMonthIndex === 0) {
      setSelectedMonthIndex(11);
      setSelectedYear(y => y - 1);
    } else {
      setSelectedMonthIndex(m => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonthIndex === 11) {
      setSelectedMonthIndex(0);
      setSelectedYear(y => y + 1);
    } else {
      setSelectedMonthIndex(m => m + 1);
    }
  };

  const createDefaultAttendees = (baseDate?: string): SafetyTalkAttendee[] => {
    return Array.from({ length: 15 }, (_, i) => ({
      index: i + 1,
      name: '',
      accepted: 'SI' as const,
      signature: '',
      date: baseDate || ''
    }));
  };

  // Open Form Modal for a specific day or new talk
  const handleOpenNewTalk = (day?: number) => {
    const targetDay = day || new Date().getDate();
    const monthStr = String(selectedMonthIndex + 1).padStart(2, '0');
    const dayStr = String(targetDay).padStart(2, '0');
    const dateStr = `${selectedYear}-${monthStr}-${dayStr}`;

    const isFuture = isDateInFuture(targetDay);
    const sedeName = activeCostCenter?.name || 'Bajío';
    const unidadDefault = sedeName.includes('-') ? sedeName.split('-')[0].trim() : 'Bajío';

    setFormTalk({
      id: `PL-${Date.now().toString().slice(-5)}`,
      costCenterId: activeCostCenter?.id || 'CC-MAZDA-BAJIO',
      costCenterName: activeCostCenter?.name || 'Sede Bajío - Salamanca',
      businessUnit: unidadDefault,
      project: unidadDefault,
      date: dateStr,
      day: targetDay,
      time: '07:30',
      shift: 'Matutino',
      topic: '',
      standardCode: 'NOM-017-STPS-2008 / EPP',
      area: 'Planta Operativa',
      facilitator: 'Alondra Tovar',
      facilitatorRole: 'Supervisor de Seguridad HSEQ',
      objective: 'El operador entiende y aplica de forma correcta la metodología de las 5S en su espacio de trabajo, así como en su vida cotidiana',
      attendeesCount: 0,
      status: isFuture ? 'programada' : 'aplicada',
      durationMinutes: 10,
      description: '',
      observations: '',
      photos: []
    });

    setAttendeesList(createDefaultAttendees(dateStr));
    setPhotosList([]);
    setIsFormModalOpen(true);
  };

  // Open Edit for an existing talk
  const handleOpenEditTalk = (talk: SafetyTalk) => {
    setFormTalk({ ...talk });
    setPhotosList(talk.photos ? [...talk.photos] : []);
    
    if (talk.attendees && talk.attendees.length > 0) {
      const atts = [...talk.attendees];
      while (atts.length < 15) {
        atts.push({
          index: atts.length + 1,
          name: '',
          accepted: 'SI',
          signature: '',
          date: talk.date || ''
        });
      }
      setAttendeesList(atts);
    } else {
      setAttendeesList(createDefaultAttendees(talk.date));
    }

    setIsFormModalOpen(true);
    setIsDetailModalOpen(false);
  };

  // Quick mark scheduled talk as completed
  const handleQuickMarkCompleted = (talk: SafetyTalk) => {
    setFormTalk({
      ...talk,
      status: 'aplicada',
      attendeesCount: talk.attendeesCount || 12
    });
    setPhotosList(talk.photos ? [...talk.photos] : []);
    if (talk.attendees && talk.attendees.length > 0) {
      setAttendeesList([...talk.attendees]);
    } else {
      setAttendeesList(createDefaultAttendees(talk.date));
    }
    setIsFormModalOpen(true);
    setIsDetailModalOpen(false);
  };

  // Handle Photo File Upload (up to 7 photos)
  const handlePhotoFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (photosList.length >= 7) {
      if (window.showAppAlert) {
        await window.showAppAlert('Se ha alcanzado el límite máximo de 7 fotos de evidencia por plática.');
      }
      return;
    }

    setIsUploadingPhoto(true);

    const availableSlots = 7 - photosList.length;
    const filesToProcess: File[] = Array.from(files).slice(0, availableSlots) as File[];

    const newPhotos: SafetyTalkPhoto[] = [];

    for (const file of filesToProcess) {
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newPhotos.push({
          id: `photo-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          name: file.name,
          dataUrl: base64,
          caption: `Evidencia #${photosList.length + newPhotos.length + 1} - ${file.name.slice(0, 16)}`,
          uploadedAt: new Date().toISOString()
        });
      } catch (err) {
        console.error('Error reading photo file:', err);
      }
    }

    setPhotosList(prev => [...prev, ...newPhotos]);
    setIsUploadingPhoto(false);

    // Reset input
    if (e.target) e.target.value = '';
  };

  // Live Camera Controls (Webcam Modal)
  const startCameraStream = async () => {
    if (photosList.length >= 7) {
      if (window.showAppAlert) {
        await window.showAppAlert('Límite de 7 fotos alcanzado.');
      }
      return;
    }

    setIsCameraActive(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.warn('Direct stream not accessible, opening native camera input fallback:', err);
      setIsCameraActive(false);
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    }
  };

  const capturePhotoFromStream = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

      const newPhoto: SafetyTalkPhoto = {
        id: `photo-cam-${Date.now()}`,
        name: `Foto_Camara_${photosList.length + 1}.jpg`,
        dataUrl,
        caption: `Evidencia Fotográfica #${photosList.length + 1}`,
        uploadedAt: new Date().toISOString()
      };

      setPhotosList(prev => [...prev, newPhoto]);
    }

    stopCameraStream();
  };

  const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  const handleRemovePhoto = (photoId: string) => {
    setPhotosList(prev => prev.filter(p => p.id !== photoId));
  };

  const handleUpdateCaption = (photoId: string, caption: string) => {
    setPhotosList(prev => prev.map(p => p.id === photoId ? { ...p, caption } : p));
  };

  // Submit Save
  const handleSaveForm = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formTalk.topic || formTalk.topic.trim().length === 0) {
      if (window.showAppAlert) window.showAppAlert('Por favor ingrese el tema de la plática.');
      return;
    }

    if (!formTalk.date) {
      if (window.showAppAlert) window.showAppAlert('Por favor seleccione la fecha de la plática.');
      return;
    }

    const dayNumber = formTalk.day || parseInt(formTalk.date.split('-')[2], 10) || 1;

    // Count how many attendees have names or keep current count
    const filledAttendees = attendeesList.filter(a => a.name && a.name.trim().length > 0);
    const countAttendees = filledAttendees.length > 0 ? filledAttendees.length : (Number(formTalk.attendeesCount) || 0);

    const finalTalk: SafetyTalk = {
      id: formTalk.id || `PL-${Date.now().toString().slice(-5)}`,
      costCenterId: activeCostCenter?.id || formTalk.costCenterId || 'CC-MAZDA-BAJIO',
      costCenterName: activeCostCenter?.name || formTalk.costCenterName || 'Sede Bajío - Salamanca',
      businessUnit: formTalk.businessUnit || 'Bajío',
      project: formTalk.project || 'Bajío',
      date: formTalk.date,
      day: dayNumber,
      time: formTalk.time || '07:30',
      shift: formTalk.shift || 'Matutino',
      topic: formTalk.topic.trim(),
      standardCode: formTalk.standardCode || 'NOM-017-STPS-2008 / EPP',
      area: formTalk.area || 'Planta Operativa',
      facilitator: formTalk.facilitator || 'Alondra Tovar',
      facilitatorRole: formTalk.facilitatorRole || 'Supervisor de Seguridad',
      objective: formTalk.objective || 'El operador entiende y aplica de forma correcta la metodología de las 5S en su espacio de trabajo, así como en su vida cotidiana',
      attendeesCount: countAttendees,
      attendees: attendeesList,
      status: formTalk.status || 'aplicada',
      durationMinutes: Number(formTalk.durationMinutes) || 10,
      description: formTalk.description || '',
      observations: formTalk.observations || '',
      photos: photosList,
      registeredBy: 'Coordinador HSEQ',
      createdAt: formTalk.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSaveTalk(finalTalk);
    setIsFormModalOpen(false);

    if (window.showAppAlert) {
      window.showAppAlert(`Plática "${finalTalk.topic}" guardada exitosamente en el calendario mensual.`);
    }
  };

  // Delete Talk
  const handleDeleteTalkConfirm = async (talkId: string) => {
    let confirm = true;
    if (window.showAppConfirm) {
      confirm = await window.showAppConfirm('¿Está seguro de eliminar esta plática del calendario mensual?');
    }
    if (confirm) {
      onDeleteTalk(talkId);
      setIsDetailModalOpen(false);
    }
  };

  // Generate Single Talk PDF
  const handleDownloadTalkPdf = async (talk: SafetyTalk) => {
    setIsGeneratingPdf(true);
    try {
      await generateSafetyTalkPDF(talk, activeCostCenter?.name || 'Sede Bajío');
    } catch (err) {
      console.error('Error generating PDF:', err);
      if (window.showAppAlert) window.showAppAlert('Ocurrió un error al generar el acta en PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Generate Monthly PDF
  const handleDownloadMonthlyPlanPdf = async () => {
    setIsGeneratingMonthlyPdf(true);
    try {
      await generateMonthlySafetyTalksPlanPDF(
        selectedMonthIndex,
        selectedYear,
        monthTalks,
        activeCostCenter?.name || 'Sede Bajío'
      );
    } catch (err) {
      console.error('Error generating monthly plan PDF:', err);
      if (window.showAppAlert) window.showAppAlert('Ocurrió un error al generar el plan mensual en PDF.');
    } finally {
      setIsGeneratingMonthlyPdf(false);
    }
  };

  // Helper for applying suggested topic to form
  const handleSelectSuggestedTopic = (sugg: typeof SUGGESTED_TOPICS[0]) => {
    setFormTalk(prev => ({
      ...prev,
      topic: sugg.topic,
      standardCode: sugg.standard,
      area: sugg.area
    }));
  };

  // Bulk import handler
  const handleBatchImport = (importedTalks: SafetyTalk[], replaceMonth: boolean) => {
    if (onBatchSaveTalks) {
      onBatchSaveTalks(importedTalks, replaceMonth, selectedYear, selectedMonthIndex);
    } else {
      importedTalks.forEach(t => onSaveTalk(t));
    }
  };

  return (
    <div id="safety-talks-planner-container" className="space-y-6">
      {/* Hidden File Inputs for photo upload and native camera */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoFilesSelected}
        accept="image/*"
        multiple
        className="hidden"
      />
      <input
        type="file"
        ref={cameraInputRef}
        onChange={handlePhotoFilesSelected}
        accept="image/*"
        capture="environment"
        className="hidden"
      />

      {/* Top Header Card with Month Navigation & View Modes */}
      <div className="bg-[#121317] border border-[#222430] rounded-xl p-5 shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Title and Cost Center */}
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-lg bg-emerald-950/60 border border-emerald-500/40 text-emerald-400">
                <Calendar className="w-5 h-5" />
              </span>
              <div>
                <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
                  Planificador Mensual y Cruz de Pláticas de 5 Minutos
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/60 text-emerald-300 border border-emerald-500/30 font-medium">
                    NOM-019-STPS
                  </span>
                </h2>
                <p className="text-xs text-slate-400">
                  Planeación de actividades mensuales, calendarización, impartición con 7 fotos de evidencia y cruz de seguridad.
                </p>
              </div>
            </div>
          </div>

          {/* Month & Year Navigator */}
          <div className="flex items-center gap-2 bg-[#1a1c23] p-1.5 rounded-xl border border-[#2e3140]">
            <button
              id="prev-month-btn"
              onClick={handlePrevMonth}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              title="Mes anterior"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-1.5 px-3">
              <span className="text-sm font-bold text-white min-w-[130px] text-center">
                {formattedMonthYear}
              </span>
            </div>

            <button
              id="next-month-btn"
              onClick={handleNextMonth}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              title="Mes siguiente"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="bg-[#121317] border border-[#2e3140] text-xs text-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-emerald-500 font-medium"
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
          </div>

          {/* Action Buttons & View Mode */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* View Mode Switcher */}
            <div className="flex items-center bg-[#1a1c23] p-1 rounded-xl border border-[#2e3140]">
              <button
                id="view-calendar-btn"
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'calendar'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Calendar className="w-3.5 h-3.5" />
                Vista Calendario
              </button>

              <button
                id="view-cross-btn"
                onClick={() => setViewMode('cross')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'cross'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Cruz de Pláticas
              </button>

              <button
                id="view-history-btn"
                onClick={() => setViewMode('history')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'history'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <FileText className="w-3.5 h-3.5" />
                Historial ({monthTalks.length})
              </button>
            </div>

            {/* Bulk Upload Button */}
            <button
              id="bulk-upload-talks-btn"
              onClick={() => setIsBulkUploadOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-[#1a1c23] hover:bg-emerald-950/40 text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 hover:border-emerald-400 transition-all shadow-sm cursor-pointer"
              title="Carga Masiva de Pláticas con Plantilla Excel / CSV"
            >
              <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
              <span>Carga Masiva (Excel)</span>
            </button>

            {/* Export Monthly Plan PDF */}
            <button
              id="export-monthly-plan-pdf-btn"
              onClick={handleDownloadMonthlyPlanPdf}
              disabled={isGeneratingMonthlyPdf}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-[#1a1c23] text-slate-200 hover:text-white hover:bg-slate-800 border border-[#2e3140] transition-colors cursor-pointer"
              title="Descargar Plan Mensual en PDF"
            >
              <Download className="w-3.5 h-3.5 text-emerald-400" />
              {isGeneratingMonthlyPdf ? 'Generando...' : 'Plan Mensual PDF'}
            </button>

            {/* Plan New Talk Button */}
            <button
              id="plan-new-talk-btn"
              onClick={() => handleOpenNewTalk()}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-md transition-all transform active:scale-95 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              Planear Plática
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-[#121317] border border-[#222430] rounded-xl p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Pláticas del Mes</span>
            <Layers className="w-4 h-4 text-slate-400" />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{metrics.totalMonth}</p>
          <p className="text-[10px] text-slate-500">Planificadas / Registradas</p>
        </div>

        <div className="bg-[#121317] border border-emerald-900/40 rounded-xl p-3.5 bg-gradient-to-b from-emerald-950/20 to-transparent">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wider">Aplicadas / Impartidas</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-emerald-300 mt-1">{metrics.applied}</p>
          <p className="text-[10px] text-emerald-400/80">Con evidencia fotográfica</p>
        </div>

        <div className="bg-[#121317] border border-blue-900/40 rounded-xl p-3.5 bg-gradient-to-b from-blue-950/20 to-transparent">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider">Programadas</span>
            <Clock className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-blue-300 mt-1">{metrics.scheduled}</p>
          <p className="text-[10px] text-blue-400/80">En calendario mensual</p>
        </div>

        <div className="bg-[#121317] border border-amber-900/40 rounded-xl p-3.5 bg-gradient-to-b from-amber-950/20 to-transparent">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">Pendientes</span>
            <AlertTriangle className="w-4 h-4 text-amber-400" />
          </div>
          <p className="text-2xl font-bold text-amber-300 mt-1">{metrics.pending}</p>
          <p className="text-[10px] text-amber-400/80">No impartidas / Vencidas</p>
        </div>

        <div className="bg-[#121317] border border-[#222430] rounded-xl p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-teal-400 uppercase tracking-wider">Cumplimiento</span>
            <Award className="w-4 h-4 text-teal-400" />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{metrics.complianceRate}%</p>
          <p className="text-[10px] text-slate-400">Tasa de aplicación</p>
        </div>

        <div className="bg-[#121317] border border-[#222430] rounded-xl p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-purple-400 uppercase tracking-wider">Asistencia Total</span>
            <Users className="w-4 h-4 text-purple-400" />
          </div>
          <p className="text-2xl font-bold text-purple-300 mt-1">{metrics.totalAttendees}</p>
          <p className="text-[10px] text-slate-400">Participaciones acum.</p>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* VISTA 1: CALENDARIO MENSUAL DE PLÁTICAS (Similar al diseño solicitado) */}
      {/* ========================================================================= */}
      {viewMode === 'calendar' && (
        <div className="bg-[#0e0f12] border border-[#222430] rounded-xl p-5 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <span>Distribución Diaria — {formattedMonthYear}</span>
                <span className="text-xs text-slate-400 font-normal">
                  (Haga clic en cualquier día para planear, consultar o cargar evidencias fotográficas)
                </span>
              </h3>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5 text-emerald-400">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                Aplicada
              </span>
              <span className="flex items-center gap-1.5 text-blue-400">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                Programada
              </span>
              <span className="flex items-center gap-1.5 text-amber-400">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                Pendiente
              </span>
              <span className="flex items-center gap-1.5 text-slate-500">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-700"></span>
                Sin Programar
              </span>
            </div>
          </div>

          {/* Grid of days (1 to 31) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 xl:grid-cols-11 gap-2.5">
            {calendarDays.map(({ day, talk, isFuture }) => {
              const hasTalk = !!talk;
              const isApplied = talk && (talk.status === 'aplicada' || talk.status === 'completada');
              const isScheduled = talk && talk.status === 'programada';
              const isMissed = talk && talk.status === 'no_realizada';

              // Styling depending on status
              let cardBg = 'bg-[#121317] border-[#222430] hover:border-slate-600';
              let badgeColor = 'text-slate-400';
              let statusLabel = isFuture ? 'Por Transcurrir' : 'Sin Programar';
              let subLabel = isFuture ? 'Día Pendiente' : 'Día Libre';
              let statusIcon = <Clock className="w-3.5 h-3.5 text-slate-600" />;

              if (isApplied) {
                cardBg = 'bg-emerald-950/30 border-emerald-500/60 hover:border-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.15)]';
                badgeColor = 'text-emerald-400';
                statusLabel = 'Plática Impartida';
                subLabel = `${talk.attendeesCount || 0} Asistentes`;
                statusIcon = <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />;
              } else if (isScheduled) {
                cardBg = 'bg-blue-950/30 border-blue-500/60 hover:border-blue-400 shadow-[0_0_12px_rgba(59,130,246,0.15)]';
                badgeColor = 'text-blue-400';
                statusLabel = 'Plática Programada';
                subLabel = talk.facilitator ? `Fac: ${talk.facilitator.slice(0, 14)}` : 'Calendarizada';
                statusIcon = <Calendar className="w-3.5 h-3.5 text-blue-400" />;
              } else if (isMissed) {
                cardBg = 'bg-amber-950/30 border-amber-500/60 hover:border-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.15)]';
                badgeColor = 'text-amber-400';
                statusLabel = 'No Realizada';
                subLabel = 'Pendiente de aplicar';
                statusIcon = <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
              }

              return (
                <div
                  key={`cal-day-${day}`}
                  id={`day-card-${day}`}
                  onClick={() => {
                    if (talk) {
                      setSelectedTalkForDetail(talk);
                      setIsDetailModalOpen(true);
                    } else {
                      handleOpenNewTalk(day);
                    }
                  }}
                  className={`group relative rounded-xl p-3 border transition-all duration-200 cursor-pointer flex flex-col justify-between min-h-[96px] ${cardBg}`}
                >
                  {/* Top Day Number & Status Icon */}
                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors">
                      {day}
                    </span>
                    <span className="opacity-80 group-hover:opacity-100 transition-opacity">
                      {statusIcon}
                    </span>
                  </div>

                  {/* Body Content */}
                  <div className="mt-1">
                    {hasTalk ? (
                      <div>
                        <p className={`text-[11px] font-bold truncate ${badgeColor}`} title={talk.topic}>
                          {talk.topic}
                        </p>
                        <p className="text-[10px] text-slate-400 truncate mt-0.5">
                          {subLabel}
                        </p>
                        {talk.photos && talk.photos.length > 0 && (
                          <div className="flex items-center gap-1 mt-1 text-[9px] text-slate-400">
                            <Camera className="w-2.5 h-2.5 text-emerald-400" />
                            <span>{talk.photos.length} fotos</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p className="text-[10px] text-slate-400 font-medium">{statusLabel}</p>
                        <p className="text-[9px] text-slate-500 mt-0.5">{subLabel}</p>
                      </div>
                    )}
                  </div>

                  {/* Hover indicator */}
                  <div className="absolute inset-0 rounded-xl bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VISTA 2: CRUZ DE PLÁTICAS DE SEGURIDAD (Geometry 31 days) */}
      {/* ========================================================================= */}
      {viewMode === 'cross' && (
        <div className="bg-[#0e0f12] border border-[#222430] rounded-xl p-6 shadow-xl">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-white flex items-center justify-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                Cruz Industrial de Pláticas de Seguridad — {formattedMonthYear}
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Visualización estandarizada de cumplimiento de charlas operativas de 5 minutos por día del mes.
              </p>
            </div>

            {/* Visual Cross Grid */}
            <div className="flex flex-col items-center gap-1.5 select-none my-4">
              {/* TOP STEM (Days 1 to 6) -> 2 cols x 3 rows */}
              <div className="grid grid-cols-2 gap-1.5">
                {[1, 2, 3, 4, 5, 6].map(day => {
                  const dayObj = calendarDays.find(d => d.day === day);
                  const talk = dayObj?.talk;
                  const isApplied = talk && (talk.status === 'aplicada' || talk.status === 'completada');
                  const isScheduled = talk && talk.status === 'programada';
                  const isMissed = talk && talk.status === 'no_realizada';

                  let cellColor = 'bg-[#181920] border-[#2e3140] text-slate-500';
                  if (isApplied) cellColor = 'bg-emerald-700 border-emerald-400 text-white font-bold shadow-[0_0_10px_rgba(16,185,129,0.3)]';
                  else if (isScheduled) cellColor = 'bg-blue-700 border-blue-400 text-white font-bold';
                  else if (isMissed) cellColor = 'bg-amber-600 border-amber-400 text-white font-bold';

                  return (
                    <button
                      key={`cross-day-${day}`}
                      onClick={() => talk ? (setSelectedTalkForDetail(talk), setIsDetailModalOpen(true)) : handleOpenNewTalk(day)}
                      className={`w-14 h-12 rounded-lg border flex flex-col items-center justify-center text-xs transition-transform hover:scale-105 ${cellColor}`}
                      title={talk ? `${day}: ${talk.topic}` : `Día ${day}`}
                    >
                      <span className="font-bold">{day}</span>
                      <span className="text-[9px] opacity-90">{isApplied ? '✓' : isScheduled ? '⏱' : isMissed ? '!' : ''}</span>
                    </button>
                  );
                })}
              </div>

              {/* HORIZONTAL CROSS BAR (Days 7 to 24) -> 6 cols x 3 rows */}
              <div className="grid grid-cols-6 gap-1.5 my-1">
                {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24].map(day => {
                  const dayObj = calendarDays.find(d => d.day === day);
                  const talk = dayObj?.talk;
                  const isApplied = talk && (talk.status === 'aplicada' || talk.status === 'completada');
                  const isScheduled = talk && talk.status === 'programada';
                  const isMissed = talk && talk.status === 'no_realizada';

                  let cellColor = 'bg-[#181920] border-[#2e3140] text-slate-500';
                  if (isApplied) cellColor = 'bg-emerald-700 border-emerald-400 text-white font-bold shadow-[0_0_10px_rgba(16,185,129,0.3)]';
                  else if (isScheduled) cellColor = 'bg-blue-700 border-blue-400 text-white font-bold';
                  else if (isMissed) cellColor = 'bg-amber-600 border-amber-400 text-white font-bold';

                  return (
                    <button
                      key={`cross-day-${day}`}
                      onClick={() => talk ? (setSelectedTalkForDetail(talk), setIsDetailModalOpen(true)) : handleOpenNewTalk(day)}
                      className={`w-14 h-12 rounded-lg border flex flex-col items-center justify-center text-xs transition-transform hover:scale-105 ${cellColor}`}
                      title={talk ? `${day}: ${talk.topic}` : `Día ${day}`}
                    >
                      <span className="font-bold">{day}</span>
                      <span className="text-[9px] opacity-90">{isApplied ? '✓' : isScheduled ? '⏱' : isMissed ? '!' : ''}</span>
                    </button>
                  );
                })}
              </div>

              {/* BOTTOM STEM (Days 25 to 31) -> 2 cols x 4 rows */}
              <div className="grid grid-cols-2 gap-1.5">
                {[25, 26, 27, 28, 29, 30, 31].map(day => {
                  if (day > daysInCurrentMonth) return null;
                  const dayObj = calendarDays.find(d => d.day === day);
                  const talk = dayObj?.talk;
                  const isApplied = talk && (talk.status === 'aplicada' || talk.status === 'completada');
                  const isScheduled = talk && talk.status === 'programada';
                  const isMissed = talk && talk.status === 'no_realizada';

                  let cellColor = 'bg-[#181920] border-[#2e3140] text-slate-500';
                  if (isApplied) cellColor = 'bg-emerald-700 border-emerald-400 text-white font-bold shadow-[0_0_10px_rgba(16,185,129,0.3)]';
                  else if (isScheduled) cellColor = 'bg-blue-700 border-blue-400 text-white font-bold';
                  else if (isMissed) cellColor = 'bg-amber-600 border-amber-400 text-white font-bold';

                  return (
                    <button
                      key={`cross-day-${day}`}
                      onClick={() => talk ? (setSelectedTalkForDetail(talk), setIsDetailModalOpen(true)) : handleOpenNewTalk(day)}
                      className={`w-14 h-12 rounded-lg border flex flex-col items-center justify-center text-xs transition-transform hover:scale-105 ${cellColor}`}
                      title={talk ? `${day}: ${talk.topic}` : `Día ${day}`}
                    >
                      <span className="font-bold">{day}</span>
                      <span className="text-[9px] opacity-90">{isApplied ? '✓' : isScheduled ? '⏱' : isMissed ? '!' : ''}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Cruz Legend & Normative Information */}
            <div className="mt-8 pt-6 border-t border-[#222430] grid grid-cols-1 sm:grid-cols-4 gap-4 text-xs">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-950/20 border border-emerald-900/40">
                <span className="w-4 h-4 rounded bg-emerald-600 flex items-center justify-center text-white font-bold text-[10px]">✓</span>
                <div>
                  <p className="font-bold text-white">Plática Aplicada</p>
                  <p className="text-[11px] text-emerald-400">Impartida y evidenciada</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-950/20 border border-blue-900/40">
                <span className="w-4 h-4 rounded bg-blue-600 flex items-center justify-center text-white font-bold text-[10px]">⏱</span>
                <div>
                  <p className="font-bold text-white">Programada</p>
                  <p className="text-[11px] text-blue-400">Calendarizada en mes</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-950/20 border border-amber-900/40">
                <span className="w-4 h-4 rounded bg-amber-600 flex items-center justify-center text-white font-bold text-[10px]">!</span>
                <div>
                  <p className="font-bold text-white">No Realizada</p>
                  <p className="text-[11px] text-amber-400">Pendiente de impartir</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50 border border-[#2e3140]">
                <span className="w-4 h-4 rounded bg-slate-700 flex items-center justify-center text-white font-bold text-[10px]">·</span>
                <div>
                  <p className="font-bold text-white">Sin Programar</p>
                  <p className="text-[11px] text-slate-400">Día libre o futuro</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* VISTA 3: HISTORIAL Y REGISTRO DETALLADO DE PLÁTICAS */}
      {/* ========================================================================= */}
      {viewMode === 'history' && (
        <div className="bg-[#121317] border border-[#222430] rounded-xl p-5 shadow-xl space-y-4">
          {/* Filter and Search Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por tema, facilitador, área o norma..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Filter className="w-3.5 h-3.5" />
                Filtrar:
              </span>
              <div className="flex items-center bg-[#1a1c23] p-1 rounded-lg border border-[#2e3140]">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    filterStatus === 'all' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Todas ({monthTalks.length})
                </button>
                <button
                  onClick={() => setFilterStatus('aplicada')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    filterStatus === 'aplicada' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Aplicadas ({metrics.applied})
                </button>
                <button
                  onClick={() => setFilterStatus('programada')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    filterStatus === 'programada' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Programadas ({metrics.scheduled})
                </button>
                <button
                  onClick={() => setFilterStatus('no_realizada')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    filterStatus === 'no_realizada' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Pendientes ({metrics.pending})
                </button>
              </div>
            </div>
          </div>

          {/* List of Talks */}
          {filteredHistoryTalks.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[#2e3140] rounded-xl bg-[#0e0f12]">
              <FileText className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-300">No se encontraron pláticas de seguridad</p>
              <p className="text-xs text-slate-500 mt-1">
                No hay pláticas que coincidan con los filtros seleccionados para {formattedMonthYear}.
              </p>
              <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
                <button
                  onClick={() => handleOpenNewTalk()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 transition-colors shadow-md cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Planear Primera Plática
                </button>
                <button
                  onClick={() => setIsBulkUploadOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-[#1b1d28] hover:bg-[#252838] border border-emerald-500/40 text-emerald-300 transition-colors cursor-pointer"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
                  Carga Masiva con Plantilla
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredHistoryTalks.map((talk) => {
                const isApplied = talk.status === 'aplicada' || talk.status === 'completada';
                const isScheduled = talk.status === 'programada';

                return (
                  <div
                    key={talk.id}
                    className="bg-[#181920] border border-[#282a36] hover:border-slate-600 rounded-xl p-4 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                  >
                    <div className="flex items-start gap-3.5 flex-1 min-w-0">
                      {/* Status Icon Indicator */}
                      <div className={`p-2.5 rounded-xl mt-0.5 ${
                        isApplied ? 'bg-emerald-950/60 border border-emerald-500/40 text-emerald-400' :
                        isScheduled ? 'bg-blue-950/60 border border-blue-500/40 text-blue-400' :
                        'bg-amber-950/60 border border-amber-500/40 text-amber-400'
                      }`}>
                        {isApplied ? <CheckCircle2 className="w-5 h-5" /> :
                         isScheduled ? <Clock className="w-5 h-5" /> :
                         <AlertTriangle className="w-5 h-5" />}
                      </div>

                      {/* Content details */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-bold text-slate-400">{talk.date}</span>
                          <span className="text-slate-600">•</span>
                          <span className="text-xs font-semibold text-slate-300">{talk.time || '07:30'} hrs</span>
                          <span className="text-slate-600">•</span>
                          <span className="text-xs text-slate-400">{talk.area}</span>

                          {/* Status Badge */}
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                            isApplied ? 'bg-emerald-950/80 text-emerald-300 border-emerald-500/40' :
                            isScheduled ? 'bg-blue-950/80 text-blue-300 border-blue-500/40' :
                            'bg-amber-950/80 text-amber-300 border-amber-500/40'
                          }`}>
                            {isApplied ? 'Aplicada' : isScheduled ? 'Programada' : 'Pendiente'}
                          </span>

                          {talk.standardCode && (
                            <span className="text-[10px] bg-slate-800 text-slate-300 px-2 py-0.5 rounded border border-slate-700">
                              {talk.standardCode}
                            </span>
                          )}
                        </div>

                        <h4 className="text-sm font-bold text-white mt-1 truncate">
                          {talk.topic}
                        </h4>

                        <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5 text-slate-500" />
                            Facilitador: <strong className="text-slate-300">{talk.facilitator}</strong>
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5 text-slate-500" />
                            Asistentes: <strong className="text-emerald-400">{talk.attendeesCount || 0}</strong>
                          </span>
                          <span className="flex items-center gap-1">
                            <Camera className="w-3.5 h-3.5 text-slate-500" />
                            Evidencias: <strong className="text-teal-400">{talk.photos?.length || 0}/7 fotos</strong>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 self-end md:self-center">
                      {isScheduled && (
                        <button
                          onClick={() => handleQuickMarkCompleted(talk)}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1 transition-colors"
                          title="Marcar como impartida y subir fotos"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Impartir
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setSelectedTalkForDetail(talk);
                          setIsDetailModalOpen(true);
                        }}
                        className="p-2 rounded-lg bg-[#222430] text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
                        title="Ver detalles y fotos"
                      >
                        <Eye className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDownloadTalkPdf(talk)}
                        className="p-2 rounded-lg bg-[#222430] text-slate-300 hover:text-emerald-400 hover:bg-slate-700 transition-colors"
                        title="Descargar Acta en PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleOpenEditTalk(talk)}
                        className="p-2 rounded-lg bg-[#222430] text-slate-300 hover:text-blue-400 hover:bg-slate-700 transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDeleteTalkConfirm(talk.id)}
                        className="p-2 rounded-lg bg-[#222430] text-slate-300 hover:text-red-400 hover:bg-slate-700 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: FORMULARIO DE PLANIFICACIÓN / REGISTRO CON 7 FOTOS */}
      {/* ========================================================================= */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="bg-[#121317] border border-[#2e3140] rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden my-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#222430] bg-[#181920]">
              <div className="flex items-center gap-2.5">
                <span className="p-2 rounded-xl bg-emerald-950 border border-emerald-500/40 text-emerald-400">
                  <Calendar className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="text-base font-bold text-white">
                    {formTalk.id ? 'Planear / Registrar Plática de Seguridad' : 'Nueva Plática de 5 Minutos'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Sede: {formTalk.costCenterName || costCenterTitle} • {formattedMonthYear}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsFormModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSaveForm} className="p-6 overflow-y-auto space-y-6 flex-1 text-xs">
              {/* Estatus & Tipo de Registro */}
              <div className="p-4 rounded-xl bg-[#181920] border border-[#282a36] space-y-3">
                <label className="text-xs font-bold text-slate-300 block">
                  Estado de la Plática:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setFormTalk(p => ({ ...p, status: 'programada' }))}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs font-bold transition-all ${
                      formTalk.status === 'programada'
                        ? 'bg-blue-950 border-blue-500 text-blue-200 shadow-md'
                        : 'bg-[#121317] border-[#2e3140] text-slate-400 hover:text-white'
                    }`}
                  >
                    <Clock className="w-4 h-4 text-blue-400" />
                    Programada / Plan
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormTalk(p => ({ ...p, status: 'aplicada' }))}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs font-bold transition-all ${
                      formTalk.status === 'aplicada' || formTalk.status === 'completada'
                        ? 'bg-emerald-950 border-emerald-500 text-emerald-200 shadow-md'
                        : 'bg-[#121317] border-[#2e3140] text-slate-400 hover:text-white'
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Aplicada / Impartida
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormTalk(p => ({ ...p, status: 'no_realizada' }))}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs font-bold transition-all ${
                      formTalk.status === 'no_realizada'
                        ? 'bg-amber-950 border-amber-500 text-amber-200 shadow-md'
                        : 'bg-[#121317] border-[#2e3140] text-slate-400 hover:text-white'
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    Pendiente / No Realizada
                  </button>
                </div>
              </div>

              {/* Suggestions Shortcuts */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-bold text-slate-300 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    Temas Sugeridos Normativos STPS (Haga clic para autocompletar):
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-1.5 bg-[#0e0f12] rounded-xl border border-[#222430]">
                  {SUGGESTED_TOPICS.map((sugg, idx) => (
                    <button
                      key={`sugg-${idx}`}
                      type="button"
                      onClick={() => handleSelectSuggestedTopic(sugg)}
                      className="px-2.5 py-1 rounded-lg bg-[#1a1c23] hover:bg-emerald-950 hover:border-emerald-500/50 border border-[#2e3140] text-[10px] text-slate-300 hover:text-emerald-300 transition-colors text-left"
                    >
                      <strong>{sugg.standard.split(' ')[0]}:</strong> {sugg.topic.slice(0, 32)}...
                    </button>
                  ))}
                </div>
              </div>

              {/* Row 1: Unidad de Negocio, Proyecto, Fecha, Hora */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Unidad de Negocio *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Bajío"
                    value={formTalk.businessUnit || ''}
                    onChange={(e) => setFormTalk(p => ({ ...p, businessUnit: e.target.value }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Proyecto *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Bajío / Mazda"
                    value={formTalk.project || ''}
                    onChange={(e) => setFormTalk(p => ({ ...p, project: e.target.value }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Fecha de la Plática *</label>
                  <input
                    type="date"
                    required
                    value={formTalk.date || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const d = parseInt(val.split('-')[2], 10) || 1;
                      setFormTalk(p => ({ ...p, date: val, day: d }));
                      setAttendeesList(prev => prev.map(a => ({ ...a, date: a.date || val })));
                    }}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Hora</label>
                  <input
                    type="time"
                    value={formTalk.time || '07:30'}
                    onChange={(e) => setFormTalk(p => ({ ...p, time: e.target.value }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>
              </div>

              {/* Row 2: Turno, Duración, Estatus, Norma STPS */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Turno</label>
                  <select
                    value={formTalk.shift || 'Matutino'}
                    onChange={(e) => setFormTalk(p => ({ ...p, shift: e.target.value }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  >
                    <option value="Matutino">Matutino (1er Turno)</option>
                    <option value="Vespertino">Vespertino (2do Turno)</option>
                    <option value="Nocturno">Nocturno (3er Turno)</option>
                    <option value="Mixto">Mixto / Especial</option>
                  </select>
                </div>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Duración (min)</label>
                  <input
                    type="number"
                    min="5"
                    max="60"
                    value={formTalk.durationMinutes || 10}
                    onChange={(e) => setFormTalk(p => ({ ...p, durationMinutes: Number(e.target.value) }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Estatus</label>
                  <select
                    value={formTalk.status || 'aplicada'}
                    onChange={(e) => setFormTalk(p => ({ ...p, status: e.target.value as SafetyTalkStatus }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  >
                    <option value="aplicada">Aplicada / Impartida</option>
                    <option value="programada">Programada</option>
                    <option value="no_realizada">No Realizada / Pendiente</option>
                  </select>
                </div>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Norma STPS / Ref.</label>
                  <input
                    type="text"
                    placeholder="Ej. NOM-017-STPS-2008"
                    value={formTalk.standardCode || ''}
                    onChange={(e) => setFormTalk(p => ({ ...p, standardCode: e.target.value }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>
              </div>

              {/* Row 3: Nombre de la Plática (Tema) */}
              <div>
                <label className="text-slate-300 font-semibold mb-1 block">Nombre de la Plática / Tema a Impartir *</label>
                <input
                  type="text"
                  required
                  placeholder="Ej. 5's en el trabajo y la vida cotidiana"
                  value={formTalk.topic || ''}
                  onChange={(e) => setFormTalk(p => ({ ...p, topic: e.target.value }))}
                  className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                />
              </div>

              {/* Row 4: Exponente / Facilitador & Área Operativa */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Exponente / Facilitador *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Alondra Tovar"
                    value={formTalk.facilitator || ''}
                    onChange={(e) => setFormTalk(p => ({ ...p, facilitator: e.target.value }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>

                <div>
                  <label className="text-slate-300 font-semibold mb-1 block">Área Operativa</label>
                  <input
                    type="text"
                    placeholder="Ej. Planta Bajío"
                    value={formTalk.area || ''}
                    onChange={(e) => setFormTalk(p => ({ ...p, area: e.target.value }))}
                    className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>
              </div>

              {/* Row 5: Objetivo de la Plática (Para Reporte Oficial) */}
              <div>
                <label className="text-slate-300 font-semibold mb-1 block">
                  Objetivo de la Plática (Para Reporte Oficial en PDF)
                </label>
                <input
                  type="text"
                  placeholder="Ej. El operador entiende y aplica de forma correcta la metodología de las 5S en su espacio de trabajo, así como en su vida cotidiana"
                  value={formTalk.objective || ''}
                  onChange={(e) => setFormTalk(p => ({ ...p, objective: e.target.value }))}
                  className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium text-xs"
                />
              </div>

              {/* Descripción y puntos clave */}
              <div>
                <label className="text-slate-300 font-semibold mb-1 block">
                  Descripción del Contenido y Puntos Clave de Seguridad
                </label>
                <textarea
                  rows={2}
                  placeholder="Describa brevemente los puntos abordados, riesgos identificados y acuerdos preventivos..."
                  value={formTalk.description || ''}
                  onChange={(e) => setFormTalk(p => ({ ...p, description: e.target.value }))}
                  className="w-full bg-[#1a1c23] border border-[#2e3140] rounded-xl p-2.5 text-white focus:outline-none focus:border-emerald-500 font-medium"
                />
              </div>

              {/* ======================================================= */}
              {/* SECCIÓN: LISTA NOMINAL DE ASISTENCIA (15 REGISTROS PARA PDF) */}
              {/* ======================================================= */}
              <div className="p-4 rounded-xl bg-[#181920] border border-[#282a36] space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h4 className="text-xs font-bold text-white flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-400" />
                      Lista Nominal de Asistencia y Aceptación (15 Registros)
                      <span className="px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-500/40 text-[10px]">
                        {attendeesList.filter(a => a.name && a.name.trim().length > 0).length} Registrados
                      </span>
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      Capture los nombres de los trabajadores para el acta oficial en PDF y su confirmación de recepción.
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsAttendeesExpanded(!isAttendeesExpanded)}
                      className="px-3 py-1.5 rounded-lg bg-[#222430] hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center gap-1.5 transition-colors border border-[#2e3140]"
                    >
                      {isAttendeesExpanded ? 'Ocultar Lista' : 'Editar Nombres (15 Renglones)'}
                    </button>
                  </div>
                </div>

                {/* Attendees Table */}
                {isAttendeesExpanded && (
                  <div className="overflow-x-auto rounded-lg border border-[#2e3140] mt-2 max-h-64 overflow-y-auto">
                    <table className="w-full text-left text-[11px] text-slate-300">
                      <thead className="bg-[#121317] text-slate-400 uppercase text-[9px] sticky top-0 border-b border-[#2e3140]">
                        <tr>
                          <th className="py-2 px-2.5 w-12 text-center">#</th>
                          <th className="py-2 px-3">Nombre del Colaborador</th>
                          <th className="py-2 px-2 text-center w-28">Recibí Info</th>
                          <th className="py-2 px-2 w-32">Fecha</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#222430]">
                        {attendeesList.map((att, idx) => (
                          <tr key={idx} className="hover:bg-white/[0.02]">
                            <td className="py-1.5 px-2.5 text-center font-bold text-slate-400">{idx + 1}</td>
                            <td className="py-1.5 px-2">
                              <input
                                type="text"
                                placeholder={`Nombre del operador #${idx + 1}`}
                                value={att.name || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setAttendeesList(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], name: val, date: next[idx].date || formTalk.date || '' };
                                    return next;
                                  });
                                }}
                                className="w-full bg-[#121317] border border-[#2e3140] rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-emerald-500"
                              />
                            </td>
                            <td className="py-1.5 px-2 text-center">
                              <select
                                value={att.accepted || 'SI'}
                                onChange={(e) => {
                                  const val = e.target.value as 'SI' | 'NO';
                                  setAttendeesList(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], accepted: val };
                                    return next;
                                  });
                                }}
                                className={`rounded px-2 py-1 text-[10px] font-bold border focus:outline-none ${
                                  att.accepted === 'SI'
                                    ? 'bg-emerald-950/70 border-emerald-500 text-emerald-300'
                                    : 'bg-red-950/70 border-red-500 text-red-300'
                                }`}
                              >
                                <option value="SI">SI</option>
                                <option value="NO">NO</option>
                              </select>
                            </td>
                            <td className="py-1.5 px-2">
                              <input
                                type="date"
                                value={att.date || formTalk.date || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setAttendeesList(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], date: val };
                                    return next;
                                  });
                                }}
                                className="w-full bg-[#121317] border border-[#2e3140] rounded px-2 py-1 text-white text-[10px] focus:outline-none focus:border-emerald-500"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ======================================================= */}
              {/* SECCIÓN DE EVIDENCIA FOTOGRÁFICA (HASTA 7 FOTOS) */}
              {/* ======================================================= */}
              <div className="p-4 rounded-xl bg-[#181920] border border-[#282a36] space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h4 className="text-xs font-bold text-white flex items-center gap-2">
                      <Camera className="w-4 h-4 text-emerald-400" />
                      Evidencia Fotográfica de la Plática
                      <span className="px-2 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-500/40 text-[10px]">
                        {photosList.length} de 7 Fotos
                      </span>
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      Tome fotos en vivo o suba evidencias (lista de firmas, facilitador, operadores en planta).
                    </p>
                  </div>

                  {/* Upload / Camera Action Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={photosList.length >= 7 || isUploadingPhoto}
                      onClick={startCameraStream}
                      className="px-3 py-1.5 rounded-lg bg-teal-700 hover:bg-teal-600 disabled:opacity-50 text-white font-bold flex items-center gap-1.5 transition-colors shadow-sm"
                    >
                      <Camera className="w-3.5 h-3.5" />
                      Tomar Foto
                    </button>

                    <button
                      type="button"
                      disabled={photosList.length >= 7 || isUploadingPhoto}
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-1.5 rounded-lg bg-[#222430] hover:bg-slate-700 disabled:opacity-50 text-white font-semibold flex items-center gap-1.5 transition-colors border border-[#2e3140]"
                    >
                      <Upload className="w-3.5 h-3.5 text-emerald-400" />
                      Subir Archivos
                    </button>
                  </div>
                </div>

                {/* Photo Previews Grid */}
                {photosList.length === 0 ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-[#2e3140] hover:border-emerald-500/50 rounded-xl p-6 text-center cursor-pointer transition-colors bg-[#0e0f12]"
                  >
                    <Upload className="w-8 h-8 text-slate-500 mx-auto mb-1.5" />
                    <p className="text-xs font-semibold text-slate-300">
                      No hay fotos adjuntas todavía
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Haga clic aquí para subir hasta 7 fotografías o use el botón de cámara
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {photosList.map((p, idx) => (
                      <div
                        key={p.id}
                        className="group relative rounded-xl border border-[#2e3140] bg-[#0e0f12] overflow-hidden flex flex-col"
                      >
                        {/* Thumbnail Image */}
                        <div className="relative aspect-video bg-black flex items-center justify-center overflow-hidden">
                          <img
                            src={p.dataUrl}
                            alt={p.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <button
                            type="button"
                            onClick={() => setLightboxPhoto(p)}
                            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                            title="Ampliar foto"
                          >
                            <Maximize2 className="w-5 h-5" />
                          </button>
                          <span className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                            #{idx + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(p.id)}
                            className="absolute top-1.5 right-1.5 bg-red-600/90 text-white p-1 rounded hover:bg-red-500 transition-colors"
                            title="Eliminar foto"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Caption input */}
                        <div className="p-1.5 bg-[#121317]">
                          <input
                            type="text"
                            placeholder="Descripción de la foto..."
                            value={p.caption || ''}
                            onChange={(e) => handleUpdateCaption(p.id, e.target.value)}
                            className="w-full bg-[#1a1c23] border border-[#2e3140] rounded px-1.5 py-1 text-[10px] text-white focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#222430]">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-[#1a1c23] text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg transition-all"
                >
                  Guardar Plática en Calendario
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: VISTA DE DETALLE COMPLETO CON GALERÍA Y ACTA PDF */}
      {/* ========================================================================= */}
      {isDetailModalOpen && selectedTalkForDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm overflow-y-auto">
          <div className="bg-[#121317] border border-[#2e3140] rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden my-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#222430] bg-[#181920]">
              <div className="flex items-center gap-3">
                <span className={`p-2.5 rounded-xl border ${
                  selectedTalkForDetail.status === 'aplicada' || selectedTalkForDetail.status === 'completada'
                    ? 'bg-emerald-950 border-emerald-500 text-emerald-400'
                    : selectedTalkForDetail.status === 'programada'
                    ? 'bg-blue-950 border-blue-500 text-blue-400'
                    : 'bg-amber-950 border-amber-500 text-amber-400'
                }`}>
                  <FileText className="w-5 h-5" />
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400">{selectedTalkForDetail.id}</span>
                    <span className="text-slate-600">•</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                      selectedTalkForDetail.status === 'aplicada' || selectedTalkForDetail.status === 'completada'
                        ? 'bg-emerald-950 text-emerald-300 border-emerald-500/40'
                        : selectedTalkForDetail.status === 'programada'
                        ? 'bg-blue-950 text-blue-300 border-blue-500/40'
                        : 'bg-amber-950 text-amber-300 border-amber-500/40'
                    }`}>
                      {selectedTalkForDetail.status === 'aplicada' || selectedTalkForDetail.status === 'completada'
                        ? 'Plática Aplicada / Impartida'
                        : selectedTalkForDetail.status === 'programada'
                        ? 'Plática Programada'
                        : 'No Realizada / Pendiente'}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-white mt-0.5">
                    {selectedTalkForDetail.topic}
                  </h3>
                </div>
              </div>

              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-5 flex-1 text-xs">
              {/* Key Attributes */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-[#181920] p-4 rounded-xl border border-[#282a36]">
                <div>
                  <span className="text-slate-400 text-[11px] block">Fecha y Hora</span>
                  <strong className="text-white text-xs">{selectedTalkForDetail.date} ({selectedTalkForDetail.time || '07:30'})</strong>
                </div>

                <div>
                  <span className="text-slate-400 text-[11px] block">Unidad / Proyecto</span>
                  <strong className="text-white text-xs">{selectedTalkForDetail.businessUnit || 'Bajío'} / {selectedTalkForDetail.project || 'Bajío'}</strong>
                </div>

                <div>
                  <span className="text-slate-400 text-[11px] block">Facilitador / Expositor</span>
                  <strong className="text-white text-xs">{selectedTalkForDetail.facilitator || 'Alondra Tovar'}</strong>
                </div>

                <div>
                  <span className="text-slate-400 text-[11px] block">Asistencia Registrada</span>
                  <strong className="text-emerald-400 text-xs">{selectedTalkForDetail.attendeesCount || 0} Colaboradores</strong>
                </div>
              </div>

              {/* Objective */}
              {selectedTalkForDetail.objective && (
                <div className="p-3.5 rounded-xl bg-emerald-950/20 border border-emerald-500/30">
                  <span className="text-emerald-400 text-[11px] font-bold block mb-0.5">Objetivo de la Plática:</span>
                  <p className="text-slate-200 text-xs leading-relaxed">{selectedTalkForDetail.objective}</p>
                </div>
              )}

              {/* Standard Code and Topic Details */}
              {selectedTalkForDetail.standardCode && (
                <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-between">
                  <span className="text-slate-400">Norma Oficial Mexicana STPS de Referencia:</span>
                  <span className="font-bold text-emerald-300">{selectedTalkForDetail.standardCode}</span>
                </div>
              )}

              {/* Description */}
              <div>
                <h4 className="font-bold text-slate-300 mb-1.5">Contenido y Puntos Clave:</h4>
                <div className="p-3.5 rounded-xl bg-[#181920] border border-[#282a36] text-slate-300 leading-relaxed">
                  {selectedTalkForDetail.description || 'Instrucción y concientización preventiva sobre actos y condiciones inseguras en el área operativa.'}
                </div>
              </div>

              {/* Photographic Gallery (Up to 7 photos) */}
              <div>
                <h4 className="font-bold text-slate-300 mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Camera className="w-4 h-4 text-emerald-400" />
                    Evidencia Fotográfica ({selectedTalkForDetail.photos?.length || 0} de 7 Fotos):
                  </span>
                  {selectedTalkForDetail.photos && selectedTalkForDetail.photos.length > 0 && (
                    <span className="text-[10px] text-slate-400 font-normal">
                      Haga clic en cualquier foto para ver a tamaño completo
                    </span>
                  )}
                </h4>

                {(!selectedTalkForDetail.photos || selectedTalkForDetail.photos.length === 0) ? (
                  <div className="p-6 rounded-xl border border-dashed border-[#2e3140] text-center text-slate-500 bg-[#0e0f12]">
                    Sin fotografías de evidencia adjuntas para esta plática.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {selectedTalkForDetail.photos.map((p, idx) => (
                      <div
                        key={p.id || idx}
                        onClick={() => setLightboxPhoto(p)}
                        className="group relative aspect-video bg-black rounded-xl overflow-hidden border border-[#2e3140] hover:border-emerald-500 cursor-pointer transition-all"
                      >
                        <img
                          src={p.dataUrl}
                          alt={p.caption || 'Evidencia'}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end p-2">
                          <p className="text-[10px] font-medium text-white truncate">
                            {p.caption || `Foto #${idx + 1}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t border-[#222430] bg-[#181920]">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDeleteTalkConfirm(selectedTalkForDetail.id)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold bg-red-950/40 text-red-400 hover:bg-red-900/60 border border-red-900/50 flex items-center gap-1.5 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Eliminar Plática
                </button>
              </div>

              <div className="flex items-center gap-2">
                {selectedTalkForDetail.status === 'programada' && (
                  <button
                    onClick={() => handleQuickMarkCompleted(selectedTalkForDetail)}
                    className="px-3.5 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1.5 transition-colors shadow-md"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Marcar como Impartida
                  </button>
                )}

                <button
                  onClick={() => handleOpenEditTalk(selectedTalkForDetail)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold bg-[#222430] text-slate-200 hover:text-white hover:bg-slate-700 transition-colors flex items-center gap-1.5"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  Editar Plática
                </button>

                <button
                  onClick={() => handleDownloadTalkPdf(selectedTalkForDetail)}
                  disabled={isGeneratingPdf}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white flex items-center gap-1.5 transition-all shadow-md"
                >
                  <Download className="w-3.5 h-3.5" />
                  {isGeneratingPdf ? 'Generando...' : 'Descargar Acta PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: WEBCAM LIVE CAMERA STREAM */}
      {/* ========================================================================= */}
      {isCameraActive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
          <div className="bg-[#121317] border border-[#2e3140] rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[#222430] bg-[#181920]">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Camera className="w-4 h-4 text-emerald-400" />
                Captura de Fotografía en Vivo
              </h3>
              <button
                onClick={stopCameraStream}
                className="p-1 rounded text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative aspect-video bg-black flex items-center justify-center overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>

            <div className="p-4 bg-[#181920] border-t border-[#222430] flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={stopCameraStream}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-[#222430] text-slate-300 hover:text-white"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={capturePhotoFromStream}
                className="px-6 py-2.5 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg flex items-center gap-2"
              >
                <Camera className="w-4 h-4" />
                Capturar Foto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 4: LIGHTBOX ZOOM PARA FOTOS */}
      {/* ========================================================================= */}
      {lightboxPhoto && (
        <div
          onClick={() => setLightboxPhoto(null)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-md cursor-pointer"
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
            <img
              src={lightboxPhoto.dataUrl}
              alt={lightboxPhoto.name}
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl border border-slate-700"
            />
            {lightboxPhoto.caption && (
              <p className="mt-3 text-sm font-semibold text-white bg-black/80 px-4 py-1.5 rounded-full border border-slate-700">
                {lightboxPhoto.caption}
              </p>
            )}
            <button
              onClick={() => setLightboxPhoto(null)}
              className="absolute -top-10 right-0 p-1.5 rounded-full bg-slate-800 text-white hover:bg-slate-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
      {/* ========================================================================= */}
      {/* MODAL 5: CARGA MASIVA DE PLÁTICAS (EXCEL / CSV) */}
      {/* ========================================================================= */}
      <SafetyTalksBulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        activeCostCenter={activeCostCenter}
        selectedYear={selectedYear}
        selectedMonthIndex={selectedMonthIndex}
        existingMonthTalks={monthTalks}
        onImportTalks={handleBatchImport}
      />
    </div>
  );
}
